import type { ReactiveSet } from '@solid-primitives/set'
import { Title } from '@solidjs/meta'
import { useSearchParams } from '@solidjs/router'
import { type Component, createEffect, createMemo, createSignal, Show } from 'solid-js'
import { createStore } from 'solid-js/store'
import Button from '~/components/Button'
import ButtonGroup from '~/components/ButtonGroup'
import Card from '~/components/Card'
import Checkbox from '~/components/Checkbox'
import Chip from '~/components/Chip'
import InfiniteList from '~/components/InfiniteList'
import SwipeAction from '~/components/SwipeAction'
import Table from '~/components/Table'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useCompanySettings } from '~/hooks/useCompanySettings'
import { useDebouncedSignal } from '~/hooks/useDebouncedSignal'
import { useIsMobile } from '~/hooks/useIsMobile'
import { usePermissions } from '~/hooks/usePermissions'
import { useServerTable } from '~/hooks/useServerTable'
import { type Path, useNavigate } from '~/router'
import { type OrderType, orderService } from '~/services/order.service'
import { resolveError } from '~/utils/error'
import { P } from '~/utils/permissions'
import { formatDate, formatMoney } from '~/utils/settings'
import { orderStatusColors, orderStatusLabels } from '~/utils/status'

/**
 * Listado de pedidos.
 *
 * A diferencia de las cotizaciones, aquí hay dinero de por medio: el estado del
 * pedido lo decide la pasarela, no el equipo. Por eso no se editan estados a
 * mano y lo único que se ofrece es cancelar los que aún nadie ha pagado.
 */
const PedidosPage: Component = () => {
  const toast = useToast()
  const alert = useAlert()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const { settings } = useCompanySettings()
  const isMobile = useIsMobile()
  const [searchParams, setSearchParams] = useSearchParams()

  const sanitizeStatus = (value: string | undefined): string => (value && value in orderStatusLabels ? value : '')

  const filterStatus = createMemo(() => sanitizeStatus(searchParams.status as string | undefined))
  const [_search, debouncedSearch, setSearch] = useDebouncedSignal('')

  // El filtro vive en la URL: compartir el enlace de "pagados" comparte el filtro.
  // Fuente única: la URL. No hay señal local + efecto de sincronización, porque
  // `setFilterStatus + setSearchParams` con el update async del router provocaba
  // un rebote pending -> paid -> pending -> paid (3 fetches por click, 6 en total
  // al alternar pestañas). Ahora solo `setSearchParams` y el memo deriva el valor.
  const handleFilterChange = (value: string) => {
    const next: Record<string, string | string[] | undefined> = { ...searchParams }
    next.status = value || undefined
    setSearchParams(next, { replace: true })
  }

  const buildParams = (): Record<string, string | number | undefined> => {
    const q = debouncedSearch().trim()
    const p: Record<string, string | number | undefined> = {}
    if (q) p.q = q
    if (filterStatus()) p.status = filterStatus()
    return p
  }

  const table = useServerTable<OrderType>({
    fetcher: params => orderService.list(params as Parameters<typeof orderService.list>[0]),
    params: buildParams
  })

  // ── Listado móvil: se acumula aparte del de escritorio ─────────────────────
  const [mobileItems, setMobileItems] = createStore<OrderType[]>([])
  const [mobileLoading, setMobileLoading] = createSignal(false)
  const [mobileTotal, setMobileTotal] = createSignal(0)
  const hasMore = () => mobileItems.length < mobileTotal()

  const mobileFetch = async (reset: boolean) => {
    if (!reset && mobileLoading()) return
    setMobileLoading(true)
    try {
      const page = reset ? 1 : Math.floor(mobileItems.length / table.perPage()) + 1
      const res = await orderService.list({ ...buildParams(), page, limit: table.perPage() })
      setMobileTotal(res.meta.total)
      setMobileItems(reset ? res.items : prev => [...prev, ...res.items])
    } catch {
      // Silencioso: el sentinela del InfiniteList reintenta al volver a entrar.
    } finally {
      setMobileLoading(false)
    }
  }

  const [filtersKey, setFiltersKey] = createSignal('')
  createEffect(() => {
    if (!isMobile()) return
    const key = JSON.stringify([filterStatus(), debouncedSearch()])
    if (key === filtersKey()) return
    setFiltersKey(key)
    void mobileFetch(true)
  })
  const loadMore = () => {
    if (!hasMore() || mobileLoading()) return
    void mobileFetch(false)
  }

  const money = (value: unknown, currency: string) => formatMoney(value as number | string, currency, settings()?.currencies ?? [])

  const handleCancel = async (order: OrderType) => {
    const ok = await alert.confirm({
      title: 'Cancelar pedido',
      message: `El pedido ${order.number} quedará cancelado. Sólo se puede con pedidos que nadie ha pagado.`,
      confirmText: 'Sí, cancelar'
    })
    if (!ok) return
    try {
      await orderService.cancel(order.id)
      toast.addToast({ title: `Pedido ${order.number} cancelado`, type: 'success' })
      setMobileItems(prev => prev.map(i => (i.id === order.id ? { ...i, status: 'cancelled' } : i)))
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  /** Le pregunta a la pasarela cómo quedó el cobro. Útil si no llegó el aviso. */
  const handleSync = async (order: OrderType) => {
    try {
      await orderService.sync(order.id)
      toast.addToast({ title: 'Estado actualizado con la pasarela', type: 'success' })
      await table.refetch()
      if (isMobile()) await mobileFetch(true)
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  // ── Selección masiva ────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = createSignal<ReadonlySet<number>>(new Set())

  const handleSelectionChange = (selected: ReactiveSet<unknown>) => {
    const next = new Set<number>()
    for (const value of selected.values()) next.add(value as number)
    setSelectedIds(next)
  }

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleBulkDelete = async () => {
    const ids = [...selectedIds()]
    if (ids.length === 0) return
    const ok = await alert.confirmDelete({
      title: 'Eliminar pedidos',
      message: `¿Eliminar ${ids.length} ${ids.length === 1 ? 'pedido' : 'pedidos'}? Los que tengan pagos se omiten: se cancelan, no se borran.`
    })
    if (!ok) return
    try {
      const res = await orderService.bulkDelete(ids)
      const skippedNote = res.skipped > 0 ? ` (${res.skipped} omitidos por tener pagos)` : ''
      toast.addToast({ title: `${res.deleted} ${res.deleted === 1 ? 'pedido eliminado' : 'pedidos eliminados'}${skippedNote}`, type: 'success' })
      setSelectedIds(new Set<number>())
      setMobileItems(prev => prev.filter(i => !ids.includes(i.id)))
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const filterOptions = createMemo(() => [
    { label: 'Todos', value: '' },
    ...Object.entries(orderStatusLabels).map(([value, label]) => ({
      label,
      value,
      color: orderStatusColors[value as keyof typeof orderStatusColors]
    }))
  ])

  const columns = [
    { header: 'Número', accessor: 'number' as const, sticky: 'left' as const, minWidth: 150 },
    {
      header: 'Cliente',
      accessor: (o: OrderType) => (
        <div>
          <div class="font-medium text-foreground">{o.customer?.name ?? o.buyer?.name ?? 'Sin cliente'}</div>
          <div class="text-xs text-foreground-subtle">{o.customer?.email ?? o.buyer?.email ?? '—'}</div>
        </div>
      )
    },
    {
      header: 'Artículos',
      accessor: (o: OrderType) => `${o.items?.length ?? 0} línea(s)`
    },
    { header: 'Total', accessor: (o: OrderType) => money(o.total, o.currency) },
    {
      header: 'Estado',
      accessor: (o: OrderType) => (
        <div class="flex flex-wrap items-center gap-1.5">
          <Chip color={orderStatusColors[o.status]} variant="soft" size="xs">
            {orderStatusLabels[o.status]}
          </Chip>
          {/* Cobrado pero sin descontar del inventario: alguien tiene que mirarlo. */}
          <Show when={o.status === 'paid' && !o.stockAppliedAt}>
            <Chip color="danger" variant="soft" size="xs">
              Sin stock aplicado
            </Chip>
          </Show>
        </div>
      )
    },
    { header: 'Fecha', accessor: (o: OrderType) => formatDate(o.createdAt, settings()?.locale) }
  ]

  return (
    <>
      <Title>Pedidos | Florez Jaramillo</Title>

      <Show when={table.error()}>
        <div class="rounded-xl border border-danger-soft bg-danger-soft/30 px-4 py-3 text-sm text-danger mb-4">{resolveError(table.error())}</div>
      </Show>

      <Show when={!isMobile()}>
        <Table<OrderType>
          title="Pedidos"
          subtitle="Compras en línea, su cobro y su estado"
          data={table.data()}
          columns={columns}
          keyField="id"
          loading={table.loading()}
          searchable
          searchPlaceholder="Buscar por número..."
          onSearch={setSearch}
          serverSide
          selectable={can(P.orders.delete)}
          selectableColor="danger"
          onSelectionChange={handleSelectionChange}
          onDeleteSelected={() => void handleBulkDelete()}
          total={table.total()}
          page={table.page()}
          perPage={table.perPage()}
          onPageChange={table.onPageChange}
          onPerPageChange={table.onPerPageChange}
          showFilters
          headSticky
          filterField="status"
          activeFilter={filterStatus()}
          onFilterChange={handleFilterChange}
          filterOptions={filterOptions()}
          emptyMessage="Todavía no hay pedidos"
          onRowClick={order => navigate(`/admin/pedidos/${order.id}` as Path)}
          actions={order => (
            <ButtonGroup size="sm" variant="soft">
              <Button color="info" onClick={() => navigate(`/admin/pedidos/${order.id}` as Path)} tooltip="Ver detalle" icon="material-symbols-light:visibility-outline" />
              <Show when={can(P.payments.read) && order.status === 'pending'}>
                <Button color="default" onClick={() => handleSync(order)} tooltip="Consultar a la pasarela" icon="material-symbols-light:sync" />
              </Show>
              <Show when={can(P.orders.update) && order.status === 'pending'}>
                <Button color="danger" onClick={() => handleCancel(order)} tooltip="Cancelar pedido" icon="material-symbols-light:cancel-outline" />
              </Show>
            </ButtonGroup>
          )}
        />
      </Show>

      <Show when={isMobile()}>
        <Show when={can(P.orders.delete) && selectedIds().size > 0}>
          <div class="mb-3 flex items-center justify-between gap-2 rounded-xl border border-danger-soft bg-danger-soft/30 px-4 py-3">
            <span class="text-sm font-semibold text-danger">
              {selectedIds().size} {selectedIds().size === 1 ? 'seleccionado' : 'seleccionados'}
            </span>
            <Button size="sm" color="danger" variant="soft" icon="material-symbols-light:delete-outline" onClick={() => void handleBulkDelete()}>
              Eliminar
            </Button>
          </div>
        </Show>
        <InfiniteList<OrderType>
          items={mobileItems}
          keyField={o => o.id}
          hasMore={hasMore()}
          loading={mobileLoading()}
          onLoadMore={loadMore}
          onRefresh={async () => {
            await mobileFetch(true)
          }}
          emptyMessage={<p class="py-10 text-center text-foreground-subtle">Todavía no hay pedidos</p>}
          endMessage={<span>Has llegado al final de los pedidos</span>}
          class="space-y-3"
          renderItem={order => (
            <SwipeAction
              itemId={order.id}
              rememberPosition
              onClick={() => navigate(`/admin/pedidos/${order.id}` as Path)}
              rightOptions={[
                ...(can(P.orders.update) && order.status === 'pending'
                  ? [
                      {
                        icon: 'material-symbols-light:cancel-outline',
                        label: 'Cancelar',
                        color: 'danger' as const,
                        action: () => handleCancel(order)
                      }
                    ]
                  : [])
              ]}
            >
              <Card class="!rounded-lg">
                <Card.Body>
                  <div class="flex items-start gap-3">
                    <Show when={can(P.orders.delete)}>
                      <Checkbox size="sm" color="danger" checked={selectedIds().has(order.id)} onChange={() => toggleSelect(order.id)} class="mt-0.5 shrink-0" />
                    </Show>
                    <div class="min-w-0 flex-1">
                      <div class="flex items-start justify-between gap-3">
                        <div class="min-w-0">
                          <p class="font-medium text-foreground">{order.number}</p>
                          <p class="text-sm text-foreground-subtle truncate">{order.customer?.name ?? order.buyer?.name ?? 'Sin cliente'}</p>
                          <p class="text-xs text-foreground-subtle">{formatDate(order.createdAt, settings()?.locale)}</p>
                        </div>
                        <div class="text-right shrink-0">
                          <p class="font-semibold text-foreground">{money(order.total, order.currency)}</p>
                          <Chip color={orderStatusColors[order.status]} variant="soft" size="xs">
                            {orderStatusLabels[order.status]}
                          </Chip>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </SwipeAction>
          )}
        />
      </Show>

      <alert.AlertComponent />
    </>
  )
}

export default PedidosPage
